/**
 * 「静止画／スクショ動画」シーンのスクリーンショット入口 (docs/features/swipe-motion-clip-plan.md §11.2.8 シーンから)。
 * URLをキャプチャ → その画像をシーンの画像に設定 (素材パネルからのドロップと同じ処理) → このシーンのスクロール動画モーダルへ進む。
 * - 開閉の状態は呼び出し側 (SceneFrameCarousel) が持つ。キャプチャ完了でシーンに画像が入り空表示が消えても閉じないため
 * - シーンの画像は1枚なので端末は1つだけ選ぶ (既定はシナリオの比率: 縦長 → スマホ・それ以外 → PC)
 * - 画像があるときは小さな「スクショを撮り直し」を出す (空のときの入口はドロップゾーン内の選択肢)
 */
import { lazy, Suspense, useState } from 'react';

import { Tooltip } from '../../../common/Tooltip';
import { fetchMaterialById, fetchMaterialSignedUrl } from '../../../../features/materials/api';
import { buildDragData, detectIsTemporary, isGcsMaterial, resolveMaterialUrls } from '../../../../features/materials/materialUrl';
import { UrlCaptureDialog } from '../../../../features/urlCapture/UrlCaptureDialog';
import { useUrlCaptureStarter } from '../../../../features/urlCapture/useUrlCaptureStarter';
import { URL_CAPTURE_SCROLL_VIDEO_TOOLTIP, type UrlCaptureJobBody } from '../../../../features/urlCapture/api';
import { useSceneHandlers } from '../hooks/SceneHandlersContext';
import type { DroppedMaterial, Scenario } from '../types';
import styles from '../ScenarioGenerator.module.css';
import '../../../../styles/buttons.css';

const SwipeClipModal = lazy(() => import('../../../../features/swipeClip/SwipeClipModal'));

type Scene = Scenario['scenes'][number];

export interface StillSceneCaptureEntryProps {
  scenario: Scenario;
  scene: Scene;
  /** ダイアログを開いているか (呼び出し側の state) */
  open: boolean;
  setOpen: (open: boolean) => void;
  /** シーンに画像があるか (あれば「スクショを撮り直し」を出す) */
  hasImage: boolean;
  /** シナリオの比率 (端末の既定値に使う) */
  aspectRatio: string;
}

async function toDroppedMaterial(materialId: string): Promise<DroppedMaterial> {
  const material = await fetchMaterialById(materialId);
  if (!material) throw new Error('キャプチャした画像が見つかりませんでした');
  const signed = isGcsMaterial(material) ? await fetchMaterialSignedUrl(materialId, detectIsTemporary(material)) : null;
  return buildDragData(material, resolveMaterialUrls(material, signed)) as DroppedMaterial;
}

export function StillSceneCaptureEntry({ scenario, scene, open, setOpen, hasImage, aspectRatio }: StillSceneCaptureEntryProps) {
  const starter = useUrlCaptureStarter();
  const { handleDropMaterial, handleGenerateSwipeClip } = useSceneHandlers();
  const [capturing, setCapturing] = useState(false);
  const [captureError, setCaptureError] = useState<string | null>(null);
  /** キャプチャ画像を設定した後のシーン (スクロール動画モーダルに渡す・BE は元画像 = シーンの画像を検証する) */
  const [swipeScene, setSwipeScene] = useState<Scene | null>(null);
  if (!starter || !handleGenerateSwipeClip) return null;

  const close = () => {
    setOpen(false);
    setCaptureError(null);
  };

  const handleScrollVideo = async (body: UrlCaptureJobBody) => {
    // 投入のエラーはダイアログが拾ってフォームに出す
    const started = await starter.start(body);
    setCaptureError(null);
    setCapturing(true);
    try {
      const summary = await started.done;
      if (!summary.primary) {
        setCaptureError(summary.failed[0]?.message ?? 'ページを取得できませんでした');
        return;
      }
      const materialId = summary.primary.materialId;
      // 素材パネルからドロップしたときと同じ処理でシーンの画像に設定する (プレビュー追加 + image_material_id 確定)
      await handleDropMaterial(scenario, scene, await toDroppedMaterial(materialId));
      setSwipeScene({ ...scene, image_material_id: materialId });
      setOpen(false);
    } catch (err) {
      setCaptureError(err instanceof Error && err.message ? err.message : 'ページを取得できませんでした');
    } finally {
      setCapturing(false);
    }
  };

  return (
    <>
      {hasImage && (
        <Tooltip content="URLのページをキャプチャして、このシーンの画像を差し替えます。続けてスクロール動画を作れます">
          <button type="button" className={styles.stillCaptureReplaceButton} onClick={() => setOpen(true)}>
            <img src="/refresh.svg" alt="" aria-hidden="true" className={styles.stillCaptureReplaceIcon} />
            スクショを撮り直し
          </button>
        </Tooltip>
      )}
      {open && (
        <UrlCaptureDialog
          title="スクリーンショットを取得"
          description="ページを上から下までキャプチャして、このシーンの画像にします。キャプチャした画像は個人フォルダにも保存されます。"
          actions={[{ label: 'スクロール動画を生成', tooltip: URL_CAPTURE_SCROLL_VIDEO_TOOLTIP, primary: true, onSubmit: handleScrollVideo }]}
          allowBoth={false}
          defaultChoice={aspectRatio === '9:16' ? 'phone' : 'pc'}
          onClose={close}
          busy={capturing}
          busyText="キャプチャしています (1〜2分ほどかかります)。閉じても処理は続き、完了すると個人フォルダに保存されます"
          error={captureError}
        />
      )}
      {swipeScene && (
        <Suspense fallback={null}>
          <SwipeClipModal
            scenario={scenario}
            scene={swipeScene}
            asin={scenario.asin ?? null}
            onClose={() => setSwipeScene(null)}
            onSubmit={(params) => handleGenerateSwipeClip(scenario, swipeScene, params)}
          />
        </Suspense>
      )}
    </>
  );
}
